import { format, startOfMonth, subMonths } from 'date-fns';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    H2Regular,
    H5Regular,
    H6Emphasized,
    SmoothieScrollView,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { ConfirmModal } from '../../common/components/ConfirmModal';
import { Button } from '../../common/components/GroveButtons';
import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import { syncteraAccountUrl } from '../../common/utils/LinkUtils';
import { requestBuilder } from '../../http/RequestBuilder';
import { GenericDataTable } from './GenericDataTable';

// Not a complete set of fields; fields listed are needed for typing
interface UnpaidRewardsByAccountMonth {
    synctera_account_id: string;
    transaction_json: object[];
    posted_year: number;
    posted_month: number;
}

const useGetUnpaidRewardsByAccountMonth = requestBuilder.query<
    UnpaidRewardsByAccountMonth[],
    { date: string }
>({
    endpoint: ({ date }) =>
        `/api/admin/bank-ops/rewards/unpaid-rewards-by-account-month/${date}`,
    queryKey: ({ date }) =>
        `/api/admin/bank-ops/rewards/unpaid-rewards-by-account-month/${date}`,
});

interface RewardMonthSummaryRow {
    cash_back_percentage: number;
    num_memberships: number;
    num_members: number;
    sum_signed_transaction_amount_cents: number;
    sum_signed_cash_back_amount_cents: number;
    num_card_transactions: number;
}

type AdminBankOpsGetRewardMonthSummaryResponseDto = RewardMonthSummaryRow[];

const useGetRewardsMonthSummary = requestBuilder.query<
    AdminBankOpsGetRewardMonthSummaryResponseDto,
    { date: string },
    Error
>({
    endpoint: ({ date }) =>
        `/api/admin/bank-ops/rewards/rewards-month-summary/${date}`,
    queryKey: ({ date }) =>
        `/api/admin/bank-ops/rewards/rewards-month-summary/${date}`,
});

export const BankOpsRewardsPanel = () => {
    const defaultDate = startOfMonth(subMonths(new Date(), 1));
    const [rewardsDate, setRewardsDate] = useState<Date>(defaultDate);

    const handleDateChange = (value: Date): void => {
        setRewardsDate(value);
    };

    const { data: rewardMonthSummary, isLoading: isLoadingRewardMonthSummary } =
        useGetRewardsMonthSummary({
            date: format(rewardsDate, 'yyyy-MM-dd'),
        });

    const { data: unpaidRewards, isLoading: isLoadingUnpaidRewards } =
        useGetUnpaidRewardsByAccountMonth({
            date: format(rewardsDate, 'yyyy-MM-dd'),
        });

    if (isLoadingRewardMonthSummary || isLoadingUnpaidRewards) {
        return <Spinner />;
    }

    return (
        <YStack gap={16}>
            <XStack
                justifyContent="space-between"
                width="100%"
                alignItems="center"
                role="banner"
            >
                <H6Emphasized>Cash Back Rewards Summary</H6Emphasized>
                <XStack alignItems="center" gap={8}>
                    <BodyMediumRegular>Month:</BodyMediumRegular>
                    <Calendar
                        value={rewardsDate}
                        onChange={(e) => {
                            handleDateChange(e.value as Date);
                        }}
                        showIcon
                        dateFormat="mm/yy"
                        view="month"
                        yearNavigator
                        yearRange="2020:2030"
                        aria-label="Select month for interest totals"
                        maxDate={startOfMonth(new Date())}
                    />
                </XStack>
            </XStack>
            <GenericDataTable data={rewardMonthSummary} />
            <XStack
                justifyContent="space-between"
                width="100%"
                alignItems="center"
                role="banner"
            >
                <H6Emphasized>Unpaid Rewards</H6Emphasized>
            </XStack>
            <UnpaidRewardsTable
                unpaidRewards={unpaidRewards ?? []}
                rewardMonth={rewardsDate}
            />
            <ReconButtonAndPopup rewardMonth={rewardsDate} />
            <CashBackPayoutButtonAndPopup rewardMonth={rewardsDate} />
        </YStack>
    );
};

const UnpaidRewardsTable = ({
    unpaidRewards,
    rewardMonth,
}: {
    unpaidRewards: UnpaidRewardsByAccountMonth[];
    rewardMonth: Date;
}) => {
    const monthDisplay = rewardMonth.toLocaleString('default', {
        month: 'long',
        year: 'numeric',
    });

    if (unpaidRewards.length === 0) {
        return (
            <BodyMediumRegular>
                No unpaid rewards for {monthDisplay}
            </BodyMediumRegular>
        );
    }

    const [selectedUnpaidReward, setSelectedUnpaidReward] =
        useState<UnpaidRewardsByAccountMonth | null>(null);

    const columnKeys = Object.keys(unpaidRewards[0] ?? {}).filter(
        (x) => x !== 'transaction_json',
    );

    return (
        <>
            <YStack gap={8}>
                <DataTable
                    value={unpaidRewards}
                    size="small"
                    tableStyle={{ width: '100%' }}
                    selectionMode="single"
                    selection={selectedUnpaidReward}
                    onSelectionChange={(e) => {
                        setSelectedUnpaidReward(
                            e.value as UnpaidRewardsByAccountMonth | null,
                        );
                    }}
                    // dataKey="id"
                    isDataSelectable={(_) => true}
                >
                    {columnKeys.map((key) => (
                        <Column key={key} field={key} header={key} />
                    ))}
                </DataTable>
            </YStack>
            <GroveSimpleModal
                isOpen={selectedUnpaidReward != null}
                onClose={() => {
                    setSelectedUnpaidReward(null);
                }}
            >
                <SmoothieScrollView maxHeight="80vh">
                    {selectedUnpaidReward != null && (
                        <ManualCashBackPayoutButtonAndPopup
                            unpaidRewards={selectedUnpaidReward}
                        />
                    )}
                </SmoothieScrollView>
            </GroveSimpleModal>
        </>
    );
};
interface AdminBankOpsGetCashBackReconResponseDto {
    cardTransactionsRecon: object;
    rewardTransactionsRecon: object;
}

const useGetCashBackRecon = requestBuilder.query<
    AdminBankOpsGetCashBackReconResponseDto,
    { date: string },
    Error
>({
    endpoint: ({ date }) =>
        `/api/admin/bank-ops/rewards/cash-back-recon/${date}`,
    queryKey: ({ date }) =>
        `/api/admin/bank-ops/rewards/cash-back-recon-month/${date}`,
});

const ReconButtonAndPopup = ({ rewardMonth }: { rewardMonth: Date }) => {
    const [isOpen, setIsOpen] = useState(false);

    const monthDisplay = rewardMonth.toLocaleString('default', {
        month: 'long',
        year: 'numeric',
    });

    return (
        <>
            <Button
                variant="primary"
                width="auto"
                size="small"
                onPress={() => {
                    setIsOpen(true);
                }}
            >
                {`Run Recon for ${monthDisplay}`}
            </Button>
            <GroveSimpleModal
                isOpen={isOpen}
                onClose={() => {
                    setIsOpen(false);
                }}
            >
                <SmoothieScrollView maxHeight="80vh">
                    <YStack
                        marginHorizontal={16}
                        marginTop={32}
                        width="90vw"
                        maxWidth={1000}
                    >
                        <H2Regular>
                            {monthDisplay} reconcilation checks
                        </H2Regular>
                        <BodySmallRegular marginVertical={16}>
                            If data does not reconcile, discuss with Bob /
                            action team.
                        </BodySmallRegular>
                        <ReconComponent rewardMonth={rewardMonth} />
                    </YStack>
                </SmoothieScrollView>
            </GroveSimpleModal>
        </>
    );
};

const ReconComponent = ({ rewardMonth }: { rewardMonth: Date }) => {
    const { data: cashBackRecon, isLoading: isLoadingCashBackRecon } =
        useGetCashBackRecon({
            date: format(rewardMonth, 'yyyy-MM-dd'),
        });

    return (
        <YStack
            gap={12}
            backgroundColor="$gray100"
            padding={16}
            borderRadius={8}
            role="region"
            aria-label="Cash Back Recon Control"
        >
            {isLoadingCashBackRecon ? (
                <Spinner />
            ) : (
                <>
                    <H6Emphasized>Card spend recon</H6Emphasized>
                    <BodyMediumRegular>
                        {JSON.stringify(
                            cashBackRecon?.cardTransactionsRecon,
                            null,
                            2,
                        )}
                    </BodyMediumRegular>
                    <H6Emphasized>Reward posting recon</H6Emphasized>
                    <BodyMediumRegular>
                        {JSON.stringify(
                            cashBackRecon?.rewardTransactionsRecon,
                            null,
                            2,
                        )}
                    </BodyMediumRegular>
                </>
            )}
        </YStack>
    );
};

const CashBackPayoutButtonAndPopup = ({
    rewardMonth,
}: {
    rewardMonth: Date;
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const monthDisplay = rewardMonth.toLocaleString('default', {
        month: 'long',
        year: 'numeric',
    });

    return (
        <>
            <Button
                variant="primary"
                width="auto"
                size="small"
                onPress={() => {
                    setIsOpen(true);
                }}
            >
                {`Run reward payout for card spend in ${monthDisplay}`}
            </Button>
            <GroveSimpleModal
                isOpen={isOpen}
                onClose={() => {
                    setIsOpen(false);
                }}
            >
                <SmoothieScrollView maxHeight="80vh">
                    <YStack marginHorizontal={16} marginTop={32}>
                        <H2Regular>
                            {monthDisplay} Card Spend - Cash Back Rewards Payout
                        </H2Regular>
                        <BodySmallRegular marginVertical={16}>
                            This should only be run by Bank Ops / Bob. It should
                            generally be run on the 22nd of the month for card
                            spend that occurred in the prior calendar month.
                        </BodySmallRegular>
                        <CashBackPayoutComponent cardSpendMonth={rewardMonth} />
                    </YStack>
                </SmoothieScrollView>
            </GroveSimpleModal>
        </>
    );
};

interface AdminBankOpsCashBackRewardPayoutRequestDto {
    yearMonth: string;
    execute: boolean;
}

interface AdminBankOpsCashBackRewardPayoutResponseDto {
    // Intentionally blank; backend response shape to be formally defined
}

const useCashBackRewardPayout = requestBuilder.mutation<
    AdminBankOpsCashBackRewardPayoutResponseDto,
    AdminBankOpsCashBackRewardPayoutRequestDto
>({
    endpoint: '/api/admin/bank-ops/rewards/payout',
    method: 'POST',
});

const ManualCashBackPayoutButtonAndPopup = ({
    unpaidRewards,
}: {
    unpaidRewards: UnpaidRewardsByAccountMonth;
}) => {
    const yearMonth = `${unpaidRewards.posted_year}-${String(unpaidRewards.posted_month).padStart(2, '0')}-01`;
    const [manualPayoutButtonDisabled, setManualPayoutButtonDisabled] =
        useState<boolean>(false);
    const [isManualPayoutConfirmModalOpen, setIsManualPayoutConfirmModalOpen] =
        useState(false);
    const [isManualPayoutExecute, setIsManualPayoutExecute] = useState(false);
    const [manualPayoutResult, setManualPayoutResult] =
        useState<AdminBankOpsManualCashBackRewardPayoutResponseDto | null>();

    const { mutateAsync: manualCashBackRewardPayout } =
        useManualCashBackRewardPayout();

    const handleManualPayoutSubmit = async () => {
        setManualPayoutButtonDisabled(true);
        setIsManualPayoutConfirmModalOpen(false);

        const manualPayoutResult = await manualCashBackRewardPayout({
            body: {
                yearMonth,
                chargeSecuredSyncteraAccountId:
                    unpaidRewards.synctera_account_id,
                includeStatementTransactionsOnly: false,
                execute: isManualPayoutExecute,
            },
        });
        setManualPayoutResult(manualPayoutResult);
    };

    return (
        <>
            <YStack
                marginHorizontal={16}
                marginTop={32}
                width="90vw"
                maxWidth={1000}
            >
                <H2Regular>
                    Pay out single account cash back reward month ({yearMonth})
                </H2Regular>
                <BodySmallRegular marginVertical={16}>
                    This should only be triggered by Fruitful Bank Ops and
                    generally only be run for account closure.
                </BodySmallRegular>
                <div>
                    <a
                        href={syncteraAccountUrl(
                            unpaidRewards.synctera_account_id,
                        )}
                        target="_blank"
                        rel="noreferrer"
                    >
                        Go to Synctera charge secured account
                    </a>
                </div>
                <H5Regular>Card transactions</H5Regular>
                <GenericDataTable data={unpaidRewards.transaction_json} />

                <XStack justifyContent="center">
                    <Button
                        onPress={(_) => {
                            setIsManualPayoutExecute(false);
                            setIsManualPayoutConfirmModalOpen(true);
                        }}
                        disabled={manualPayoutButtonDisabled}
                        variant="primary"
                        width="auto"
                        size="small"
                    >
                        Issue out-of-bound cash back reward (execute=false)
                    </Button>
                    &nbsp;
                    <Button
                        onPress={(_) => {
                            setIsManualPayoutExecute(true);
                            setIsManualPayoutConfirmModalOpen(true);
                        }}
                        disabled={manualPayoutButtonDisabled}
                        variant="primary"
                        width="auto"
                        size="small"
                    >
                        Issue out-of-bound cash back reward (execute=true)
                    </Button>
                </XStack>
                <BodyMediumRegular></BodyMediumRegular>
                {manualPayoutResult !== null && (
                    <BodyMediumRegular>
                        {JSON.stringify(manualPayoutResult, null, 2)}
                    </BodyMediumRegular>
                )}
            </YStack>

            <ConfirmModal
                isOpen={isManualPayoutConfirmModalOpen}
                onClose={() => {
                    setIsManualPayoutConfirmModalOpen(false);
                }}
                onConfirm={handleManualPayoutSubmit}
            />
        </>
    );
};

interface AdminBankOpsManualCashBackRewardPayoutRequestDto {
    yearMonth: string;
    chargeSecuredSyncteraAccountId: string;
    includeStatementTransactionsOnly: boolean;
    execute: boolean;
}

interface AdminBankOpsManualCashBackRewardPayoutResponseDto {
    // Intentionally blank; backend response shape to be formally defined
}

const useManualCashBackRewardPayout = requestBuilder.mutation<
    AdminBankOpsManualCashBackRewardPayoutResponseDto,
    AdminBankOpsManualCashBackRewardPayoutRequestDto
>({
    endpoint: '/api/admin/bank-ops/rewards/payout-manual',
    method: 'POST',
});

interface AdminBankOpsCashBackRewardPushNotificationRequestDto {
    yearMonth: string;
}

interface AdminBankOpsCashBackRewardPushNotificationResponseDto {
    yearMonth: string;
    numSuccess: number;
    numFailure: number;
    numSkipped: number;
}

const useCashBackRewardPushNotification = requestBuilder.mutation<
    AdminBankOpsCashBackRewardPushNotificationResponseDto,
    AdminBankOpsCashBackRewardPushNotificationRequestDto
>({
    endpoint: '/api/admin/bank-ops/rewards/push-notifications',
    method: 'POST',
});

const CashBackPayoutComponent = ({
    cardSpendMonth,
}: {
    cardSpendMonth: Date;
}) => {
    const [payoutButtonDisabled, setPayoutButtonDisabled] =
        useState<boolean>(false);
    const [payoutResult, setPayoutResult] =
        useState<AdminBankOpsCashBackRewardPayoutResponseDto | null>();

    const { mutateAsync: cashBackRewardPayout } = useCashBackRewardPayout();

    const handlePayoutSubmit = async () => {
        setPayoutButtonDisabled(true);

        const payoutResult = await cashBackRewardPayout({
            body: {
                yearMonth: format(cardSpendMonth, 'yyyy-MM-dd'),
                execute: true,
            },
        });
        setPayoutResult(payoutResult);
    };

    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [pushNotificationButtonDisabled, setPushNotificationButtonDisabled] =
        useState<boolean>(false);
    const [pushNotificationResult, setPushNotificationResult] =
        useState<AdminBankOpsCashBackRewardPushNotificationResponseDto | null>();

    const { mutateAsync: cashBackRewardPushNotification } =
        useCashBackRewardPushNotification();

    const handlePushNotificationSubmit = async () => {
        setIsConfirmModalOpen(false);
        setPushNotificationButtonDisabled(true);

        const pushNotificationResult = await cashBackRewardPushNotification({
            body: {
                yearMonth: format(cardSpendMonth, 'yyyy-MM-dd'),
            },
        });
        setPushNotificationResult(pushNotificationResult);
    };

    return (
        <YStack
            gap={12}
            padding={16}
            borderRadius={8}
            role="region"
            aria-label="Cash Back Payout Control"
        >
            <XStack justifyContent="center">
                <Button
                    variant="primary"
                    width="auto"
                    size="small"
                    disabled={payoutButtonDisabled}
                    onPress={handlePayoutSubmit}
                >
                    {payoutButtonDisabled && payoutResult != null
                        ? 'Run Payout (execute=true) complete'
                        : payoutButtonDisabled
                          ? 'Running....'
                          : 'Run payout (execute=true)'}
                </Button>
            </XStack>

            {payoutResult !== null && (
                <BodyMediumRegular>
                    {JSON.stringify(payoutResult, null, 2)}
                </BodyMediumRegular>
            )}

            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={() => {
                    setIsConfirmModalOpen(false);
                }}
                onConfirm={handlePushNotificationSubmit}
            />
            <H2Regular>Send cash back rewards push notifications</H2Regular>
            <BodySmallRegular marginVertical={12}>
                This should only be triggered by Fruitful Bank Ops.
            </BodySmallRegular>
            <XStack justifyContent="center">
                <Button
                    variant="primary"
                    width="auto"
                    size="small"
                    disabled={pushNotificationButtonDisabled}
                    onPress={(_) => {
                        setIsConfirmModalOpen(true);
                    }}
                >
                    {pushNotificationButtonDisabled &&
                    pushNotificationResult != null
                        ? 'Send push notifications complete'
                        : pushNotificationButtonDisabled
                          ? 'Running....'
                          : 'Send push notifications'}
                </Button>
            </XStack>
            {pushNotificationResult !== null && (
                <BodyMediumRegular>
                    {JSON.stringify(pushNotificationResult, null, 2)}
                </BodyMediumRegular>
            )}
        </YStack>
    );
};
